/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext } from 'react';
import { useDragLayer, useDrop } from 'react-dnd';
import tinycolor from 'tinycolor2';

import MyTaskKanbanStatusCard from '@totem/components/ticketing/myTickets/myTasks/myTaskKanbanStatusCard';
import MyTaskKanbanTaskCard from '@totem/components/ticketing/myTickets/myTasks/MyTaskKanbanTaskCard';
import MyTicketsContext from '@totem/components/ticketing/myTickets/myTicketsContext';
import {
  Task,
  TASK_TYPE_GROUP,
  TASK_TYPE_TASK,
} from '@totem/components/ticketing/ticketing';
import { isNotNull } from '@totem/utilities/common';

type Props = {
  status: string;
  color: string;
};

const MyTaskKanbanStatusColumn = ({ status, color }: Props) => {
  const { myTasksExtended, taskWorkflow, onTaskStatusChange } =
    useContext(MyTicketsContext);

  const onDrop = (task: Task) => {
    const currentStatus = taskWorkflow.find((chk) => chk.code === task.status);
    if (isNotNull(currentStatus)) {
      if (currentStatus.options.includes(status)) {
        // Send Update Task Status
        onTaskStatusChange(status, task);
      }
    }
  };

  const [{ isOver, isDragging }, dropRef] = useDrop({
    accept: TASK_TYPE_TASK,
    drop: (item: Task) => {
      onDrop(item);
    },
    canDrop: (item: Task) => {
      const currentStatus = taskWorkflow.find(
        (chk) => chk.code === item.status,
      );
      const allowed = currentStatus?.options.includes(status);
      return allowed;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }), // Are we hovering this drop zone
      isDragging: !!monitor.getItemType(), // Is *any* drag happening
    }),
  });

  const coreStatuses = [
    'PENDING',
    'SCHEDULED',
    'IN PROGRESS',
    'REVIEW',
    'COMPLETED',
  ];
  const statusTask = myTasksExtended.filter(
    (chk) => chk.status === status && chk.type !== TASK_TYPE_GROUP,
  );

  const isDraggingTasks = useDragLayer((monitor) => {
    const isTaskDragging =
      monitor.isDragging() && monitor.getItemType() === TASK_TYPE_TASK;
    let isStatusDroppable = false;
    if (isTaskDragging) {
      const taskItem = monitor.getItem<Task>();

      const currentStatus = taskWorkflow.find(
        (chk) => chk.code === taskItem.status,
      );
      if (isNotNull(currentStatus)) {
        if (currentStatus.options.includes(status)) {
          isStatusDroppable = true;
        }
      }
    }

    return isTaskDragging && isStatusDroppable;
  });

  const background = isDraggingTasks
    ? tinycolor.mix(color, '#FFFFFF', 80).toHexString()
    : null;

  return (
    <div>
      {isNotNull(statusTask) &&
        (statusTask.length > 0 ||
          coreStatuses.includes(status) ||
          isDraggingTasks) && (
          <div
            style={{ marginBottom: '16px', backgroundColor: background }}
            ref={dropRef}
          >
            <MyTaskKanbanStatusCard status={status} color={color} />
            {statusTask.map((tsk) => (
              <MyTaskKanbanTaskCard key={tsk.id} color={color} task={tsk} />
            ))}
            {isDraggingTasks && <div style={{ height: '32px' }} />}
          </div>
        )}
    </div>
  );
};

export default MyTaskKanbanStatusColumn;
